/**
 * Чаты после перехода на канальную схему.
 *
 * Канальный чат (CHANNEL) определяется тройкой «канал + паблишер + рекламодатель»,
 * канал — парой placeId + social (id каналов в разных соцсетях независимы).
 * В канальный чат привязываются все публикации по этому каналу, а создание
 * публикации приходит в ленту системным сообщением.
 *
 * Старые чаты по одной публикации (PUBLICATION) и прямые (DIRECT) остаются.
 */

import dayjs, { type Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

export type ChatKind = "CHANNEL" | "PUBLICATION" | "DIRECT";
export type ChatSocial = "TELEGRAM" | "VK" | "MAX";
export type PlaceSocial = "telegram" | "vk" | "max";

export interface ChatPlaceRef {
  id: number;
  social: ChatSocial;
}

export interface ChatPublicationLink {
  id: number;
  linkedAt?: string | null;
  publicationInfo?: any;
}

// Бэк принимает social в любом регистре, но отдаёт в верхнем
export const toChatSocial = (social?: string | null): ChatSocial => {
  const value = String(social || "").toUpperCase();
  if (value === "VK" || value === "VKONTAKTE") return "VK";
  if (value === "MAX") return "MAX";
  return "TELEGRAM";
};

// social канала: из поля, а если его нет — по ссылке
export const getPlaceChatSocial = (place: any): ChatSocial => {
  if (place?.social) return toChatSocial(place.social);
  const url = String(place?.url || "").toLowerCase();
  if (url.includes("vk.com") || url.includes("vk.ru")) return "VK";
  if (url.includes("max.ru")) return "MAX";
  return "TELEGRAM";
};

/** Ответ POST/GET /chat/channel: чат лежит либо в корне, либо в data. */
export const unwrapChat = (response: any) => (response?.data?.id ? response.data : response?.id ? response : null);

// Сервисы каналов ждут social в нижнем регистре
export const toPlaceSocial = (social?: string | null): PlaceSocial =>
  toChatSocial(social).toLowerCase() as PlaceSocial;

export const getChatKind = (chat: any): ChatKind => {
  if (chat?.kind) return chat.kind;
  if (chat?.place) return "CHANNEL";
  return chat?.publicationId ? "PUBLICATION" : "DIRECT";
};

/** Канал чата: у канального — поле place, у старого — канал его публикации. */
export const getChatPlaceRef = (chat: any): ChatPlaceRef | null => {
  const place = chat?.place?.id ? chat.place : chat?.publicationInfo?.place;
  if (!place?.id) return null;
  return { id: Number(place.id), social: toChatSocial(place.social) };
};

export const getChatPlaceKey = (ref: ChatPlaceRef) => `${ref.social}:${ref.id}`;

/** Публикации чата в порядке привязки. У старого чата — единственная его публикация. */
export const getChatPublications = (chat: any): ChatPublicationLink[] => {
  if (Array.isArray(chat?.publications) && chat.publications.length) return chat.publications;
  if (chat?.publicationId) {
    return [
      { id: chat.publicationId, linkedAt: chat.created, publicationInfo: chat.publicationInfo },
    ];
  }
  return [];
};

export const isSystemMessage = (message: any) => message?.type === "SYSTEM";

export type PublicationStatusTone = "neutral" | "progress" | "attention" | "success" | "danger";

const PUBLICATION_STATUSES: Record<string, { text: string; tone: PublicationStatusTone }> = {
  new: { text: "На модерации", tone: "neutral" },
  "advertiser-corrected": { text: "На модерации", tone: "neutral" },
  "admin-approved": { text: "Ждём паблишера", tone: "progress" },
  "publisher-approved": { text: "В работе", tone: "progress" },
  "link-provided": { text: "Ссылка отправлена", tone: "attention" },
  "advertiser-link-rejected": { text: "На доработке", tone: "attention" },
  "advertiser-link-approved": { text: "Завершена", tone: "success" },
  "advertiser-timeout": { text: "Завершена", tone: "success" },
  "admin-rejected": { text: "Отклонена модерацией", tone: "danger" },
  "publisher-rejected": { text: "Отклонена паблишером", tone: "danger" },
  "publisher-decision-timeout": { text: "Не взята в работу", tone: "danger" },
  "publisher-set-link-timeout": { text: "Ссылка не отправлена", tone: "danger" },
  cancel: { text: "Отменена", tone: "danger" },
};

export const PUBLICATION_STATUS_CLASSES: Record<PublicationStatusTone, string> = {
  neutral: "bg-[#F1F4F9] text-[#6B7A89]",
  progress: "bg-primary/10 text-primary",
  attention: "bg-[#FFF2E5] text-[#F28A1D]",
  success: "bg-[#E6F9F0] text-[#1FA968]",
  danger: "bg-[#FDEBEE] text-[#F8465B]",
};

// Даты и время в расписании приходят в UTC — как и в карточке заказа у паблишера
const toLocal = (date: string, time?: string | null) =>
  dayjs.utc(time ? `${date} ${time}` : date).local().locale("ru");

// Тире без переноса по краям (U+2060): «12:00–14:00» не рвётся на две строки
const NO_BREAK_DASH = "\u2060–\u2060";

// Числами, как в панели публикации: «23.09.2026», «23–27.09.2026»,
// «30.09–02.10.2026» — в карточке у номера такая дата не переносится
const formatDays = (begin: Dayjs, end: Dayjs) => {
  if (begin.isSame(end, "day")) return begin.format("DD.MM.YYYY");
  if (begin.isSame(end, "month")) return `${begin.format("DD")}${NO_BREAK_DASH}${end.format("DD.MM.YYYY")}`;
  if (begin.isSame(end, "year")) return `${begin.format("DD.MM")}${NO_BREAK_DASH}${end.format("DD.MM.YYYY")}`;
  return `${begin.format("DD.MM.YYYY")}${NO_BREAK_DASH}${end.format("DD.MM.YYYY")}`;
};

// Слот расписания — дни и окно времени: «12.10.2026, 10:00–12:00»
const formatSlot = (slot: any) => {
  const begin = toLocal(slot.dateBegin, slot.timeBegin);
  const end = toLocal(slot.dateEnd || slot.dateBegin, slot.timeEnd || slot.timeBegin);
  const days = formatDays(begin, end);
  if (!slot.timeBegin) return days;
  const from = begin.format("HH:mm");
  const to = end.format("HH:mm");
  return `${days}, ${from === to ? from : `${from}${NO_BREAK_DASH}${to}`}`;
};

export interface PublicationField {
  label: string;
  value: string;
  // В короткой строке списка показывать с подписью: голая дата непонятна
  labelInline?: boolean;
}

// Когда выходит пост: факт публикации, иначе желаемое время из расписания
const getPublicationDate = (info: any): PublicationField | null => {
  if (info?.date?.publisherPost) {
    const posted = toLocal(info.date.publisherPost);
    return { label: "Опубликована", value: formatDays(posted, posted), labelInline: true };
  }
  const slot = info?.schedule?.[0];
  if (!slot?.dateBegin) return null;
  return { label: "Размещение", value: formatSlot(slot), labelInline: true };
};

/**
 * Публикация из системного сообщения о её создании. Бэк кладёт полный
 * publicationInfo прямо в сообщение; в старых сообщениях его нет — тогда
 * берём то, что есть в карточке чата.
 */
const getMessagePublication = (message: any, chat?: any): ChatPublicationLink | null => {
  if (!isSystemMessage(message) || message?.systemEvent !== "PUBLICATION_CREATED") return null;
  const id = Number(message.publicationInfo?.id ?? message.payload?.publicationId);
  if (!id) return null;
  const link = getChatPublications(chat).find((item) => Number(item.id) === id);
  return {
    id,
    linkedAt: link?.linkedAt ?? message.created,
    publicationInfo: { ...link?.publicationInfo, ...message.publicationInfo },
  };
};

/** Поля сообщения, которые нужно сохранить при переупаковке для ленты. */
export const pickMessageMeta = (message: any) => ({
  type: message?.type ?? "USER",
  systemEvent: message?.systemEvent ?? null,
  payload: message?.payload ?? null,
  publicationInfo: message?.publicationInfo ?? null,
});

export interface PublicationMention {
  id: number | string;
  label: string;
  status?: { text: string; tone: PublicationStatusTone } | null;
  fields?: PublicationField[];
}

/** Карточка публикации для упоминаний: название, статус и короткая сводка. */
const getPublicationMention = (link: ChatPublicationLink): PublicationMention => {
  const info = link.publicationInfo;
  const fields = [
    info?.priceName ? { label: "Формат", value: info.priceName } : null,
    info?.price != null
      ? { label: "Стоимость", value: `${Number(info.price).toLocaleString("ru-RU")} ₽` }
      : null,
    getPublicationDate(info),
  ].filter(Boolean) as PublicationField[];
  return {
    id: link.id,
    label: info?.order?.name ?? "",
    status: PUBLICATION_STATUSES[info?.status] ?? null,
    fields,
  };
};

/**
 * Публикации чата для упоминаний через @, свежие сверху. Полные данные
 * приходят в системных сообщениях о создании публикации; публикации без
 * такого сообщения (старые чаты) берём из карточки чата.
 */
export const getPublicationMentions = (chat: any, messages: any[] = []): PublicationMention[] => {
  const links = new Map<number, ChatPublicationLink>();
  getChatPublications(chat).forEach((link) => links.set(Number(link.id), link));
  messages.forEach((message) => {
    const publication = getMessagePublication(message, chat);
    if (publication) links.set(publication.id, publication);
  });
  return [...links.values()].map(getPublicationMention).reverse();
};

/** Публикация из системного сообщения о её создании — для карточки в ленте. */
export const getSystemMessagePublication = (message: any, chat?: any): PublicationMention | null => {
  const publication = getMessagePublication(message, chat);
  return publication ? getPublicationMention(publication) : null;
};

/** Короткая строка о публикации: «36 000 ₽ · Размещение 12.10.2026, 10:00–12:00». */
export const getPublicationSummary = (item: PublicationMention) =>
  (item.fields ?? [])
    .map((field) => (field.labelInline ? `${field.label} ${field.value}` : field.value))
    .join(" · ");
